/**
 * DownloadAll Component - Simplified Version
 * 
 * A simplified bulk download component that aggregates content for download.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Download as DownloadIcon,
} from '@mui/icons-material';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../../../services';

interface DownloadAllProps {
  // Query configuration
  filters?: {
    contentTypes?: string[];
    tags?: string[];
    status?: string[];
    dateRange?: {
      start?: string;
      end?: string;
    };
    authors?: string[];
  };
  searchText?: string;
  limit?: number;
  contentIds?: string[];

  // Display options
  variant?: 'button' | 'card';
  showPreview?: boolean;

  // Context
  organizationId?: string;
  teamId?: string;
  userId?: string;

  // Callbacks
  onDownloadStart?: () => void;
  onDownloadComplete?: (files: string[]) => void;
  onDownloadError?: (error: string) => void;

  // Styling
  title?: string;
  description?: string;
  buttonLabel?: string;
  buttonVariant?: 'contained' | 'outlined' | 'text';
}

export const DownloadAll: React.FC<DownloadAllProps> = ({
  filters,
  searchText,
  limit = 50,
  contentIds,
  variant = 'button',
  organizationId,
  teamId,
  userId,
  onDownloadStart,
  onDownloadComplete,
  onDownloadError,
  title = 'Download All Content',
  description = 'Download all matching content files',
  buttonLabel = 'Download All',
  buttonVariant = 'contained',
}) => {
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Load content on mount
  useEffect(() => {
    loadContent();
  }, [filters, searchText, contentIds, organizationId, teamId]);

  const loadContent = async () => {
    if (!organizationId && !teamId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      let items: any[] = [];
      
      if (contentIds && contentIds.length > 0) {
        // Load specific content items with team context for subscription validation
        const promises = contentIds.slice(0, limit).map(async (id) => {
          try {
            const response = await contentService.getContent(id, teamId);
            return response;
          } catch (err) {
            console.error(`Failed to load content ${id}:`, err);
            return null;
          }
        });
        
        const results = await Promise.allSettled(promises);
        items = results
          .filter((result): result is PromiseFulfilledResult<any> => 
            result.status === 'fulfilled' && result.value !== null
          )
          .map(result => result.value);
      } else {
        // Load content based on filters
        const queryParams: any = {
          organization_id: organizationId,
          team_id: teamId,
          limit,
        };

        if (filters?.contentTypes?.length) {
          queryParams.content_types = filters.contentTypes;
        }
        if (filters?.tags?.length) {
          queryParams.tags = filters.tags;
        }
        if (searchText) {
          queryParams.search = searchText;
        }

        const response = await contentService.listContent(queryParams);
        items = response.content || [];
      }
      
      // Filter to only downloadable items
      const downloadableItems = items.filter((item: any) => 
        item.permissions?.canDownload !== false
      );

      setContent(downloadableItems);
    } catch (err: any) {
      console.error('Error loading content for download:', err);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (content.length === 0) return;
    
    setLoading(true);
    
    if (onDownloadStart) {
      onDownloadStart();
    }

    try {
      const downloadedFiles: string[] = [];
      
      // Download each file individually (no bulk API available)
      for (const item of content) {
        try {
          await contentService.downloadContentFile(item.id, item.teamId || item.team_id || '');
          downloadedFiles.push(item.title || item.id);
        } catch (err) {
          console.error(`Failed to download ${item.id}:`, err);
        }
      }

      if (onDownloadComplete) {
        onDownloadComplete(downloadedFiles);
      }
    } catch (err: any) {
      console.error('Download failed:', err);
      const errorMessage = err.message || 'Download failed';
      setError(errorMessage);
      
      if (onDownloadError) {
        onDownloadError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'button') {
    return (
      <Button
        variant={buttonVariant}
        startIcon={loading ? <CircularProgress size={16} /> : <DownloadIcon />}
        onClick={handleDownload}
        disabled={loading || content.length === 0}
      >
        {loading ? 'Downloading...' : `${buttonLabel} (${content.length})`}
      </Button>
    );
  }

  // Card variant
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        
        {description && (
          <Typography variant="body2" color="text.secondary" paragraph>
            {description}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Loading downloadable content...
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Found {content.length} downloadable files
            </Typography>
            
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={content.length === 0}
              fullWidth
            >
              Download All ({content.length} files)
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DownloadAll;